// ** React Imports
import { createContext, useEffect, useState } from 'react'

// ** Next Import
import { useRouter } from 'next/router'

// ** Axios
import axios from 'axios'

// ** Config
import authConfig from 'src/configs/auth'
import { clearReviewFormDraftsForLogout } from 'src/utils/reviewFormDraft'

const bUrl = process.env.NEXT_PUBLIC_BASE_URL
const clientURL = process.env.NEXT_PUBLIC_CLIENT_URL

// ** Defaults
const defaultProvider = {
  user: null,
  loading: true,
  setUser: () => null,
  setLoading: () => Boolean,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
}
const AuthContext = createContext(defaultProvider)

// request
import { getUserInfo, userDeviceCurrent, getNotBindOrder } from '/src/@api/login'
import { getUserDeviceList } from 'src/utils/userDeviceList'
import { handleAppDeviceBridge } from 'src/utils/appDeviceBridge'
import { bootstrapDeviceSelection } from 'src/utils/bootstrapDeviceSelection'
import {
  isSelectableDevice,
  resolveInitialEntry
} from 'src/utils/subscriptionStatus'
import { store } from 'src/store'
import { isBindingFlowPath } from 'src/utils/leaveBindingFlow'
import {
  EXPIRED_PURCHASE_PATH,
  isExpiredPurchaseRedirectExcludedPath
} from 'src/components/expired-purchase-guide/constants'
import { filterValidUnboundOrders } from 'src/utils/chooseDeviceRedirect'
import { loadUserEntryFlags, resolveUserEntry, USER_ENTRY } from 'src/utils/userEntryGate'

const getParams = url => {
  try {
    url = url.match(/\?([^#]+)/)[1]
    const obj = {}
    const arr = url.split('&')
    for (let i = 0; i < arr.length; i++) {
      let subArr = arr[i].split('=')
      let key = decodeURIComponent(subArr[0])
      let value = decodeURIComponent(subArr[1])
      obj[key] = value
    }

    return obj
  } catch (err) {
    return null
  }
}

const readStoredJson = (key, fallback = null) => {
  try {
    const raw = localStorage.getItem(key)

    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

const getDeviceId = device => device?.id ?? device?.device_id ?? ''

const USER_DEVICE_RETRY_BASE_DELAY_MS = 1000
const USER_DEVICE_RETRY_MAX_DELAY_MS = 30000
const USER_DEVICE_REQUEST_TIMEOUT_MS = 60000

const wait = delay => new Promise(resolve => setTimeout(resolve, delay))

/**
 * 保持认证 loading 直到 user-device 返回有效列表；失败时退避后持续重试。
 * 接口成功返回空数组仍视为有效响应，由后续设备选择逻辑处理空设备状态。
 */
const getUserDevicesWithRetry = async () => {
  let attempt = 0

  while (true) {
    try {
      const response = await getUserDeviceList({
        forceRefresh: true,
        requestConfig: { timeout: USER_DEVICE_REQUEST_TIMEOUT_MS }
      })
      if (Array.isArray(response?.data)) return response

      throw new Error('Unexpected user-device response')
    } catch (error) {
      attempt += 1
      const delay = Math.min(
        USER_DEVICE_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1),
        USER_DEVICE_RETRY_MAX_DELAY_MS
      )
      console.warn(
        `[AuthContext] user-device request failed; retrying in ${delay}ms`,
        error?.response?.status || error?.message
      )
      await wait(delay)
    }
  }
}

/**
 * 直达功能页并刷新时，Redux 会重新初始化，而设备选择原本只在首页完成。
 * 在放开受保护页面渲染前恢复当前设备，避免短暂落入「绑定设备」状态。
 */
const restoreDeviceSelection = async () => {
  const response = await getUserDevicesWithRetry()
  const devices = response.data
  const currentMode = localStorage.getItem('currentMode') || ''
  const savedDeviceUser = readStoredJson('DeviceUser', {}) || {}
  const savedCurrentDevice = readStoredJson('currentDevice', {}) || {}
  const savedDeviceId = savedDeviceUser.device_id ?? savedDeviceUser.id

  const matchedDevice = devices.find(device => {
    const sameId =
      savedDeviceId != null &&
      savedDeviceId !== '' &&
      String(getDeviceId(device)) === String(savedDeviceId)
    const sameUsername =
      !sameId &&
      savedDeviceUser.username &&
      device.iCloud_username === savedDeviceUser.username

    return (sameId || sameUsername) && (!currentMode || device.platform_type === currentMode)
  })

  // 仅恢复仍可选（active/grace）的保存设备；永不回退到超 30 天设备
  const preferredDevice =
    matchedDevice && isSelectableDevice(matchedDevice) ? matchedDevice : null

  let validUnbound = []
  try {
    const notBindOrder = await getNotBindOrder().catch(() => ({ data: [] }))
    validUnbound = filterValidUnboundOrders(notBindOrder?.data)
  } catch {
    validUnbound = []
  }

  const flags = await loadUserEntryFlags()
  const entryType = resolveUserEntry({ flags, devices, validUnboundOrders: validUnbound })

  // 全部订单过期超 30 天 → 过期页；未购买的跳转由首页统一处理，深层页面不强制改道
  if (entryType === USER_ENTRY.EXPIRED) {
    // User Guide / settings 等白名单页允许访问，勿踢回续费页
    if (
      typeof window !== 'undefined' &&
      !window.location.pathname.includes('expired-purchase') &&
      !isExpiredPurchaseRedirectExcludedPath(window.location.pathname)
    ) {
      window.location.assign(EXPIRED_PURCHASE_PATH)
    }
    localStorage.setItem('allDeviceUser', JSON.stringify(devices))

    return
  }

  const entry = resolveInitialEntry({
    devices,
    validUnboundOrders: validUnbound,
    preferredMode: currentMode || null,
    preferredDevice
  })

  if (entry.type !== 'device' || !entry.device) {
    // 无可选绑定设备：清空选中，避免残留 lapsed DeviceUser
    localStorage.setItem('allDeviceUser', JSON.stringify(devices))
    bootstrapDeviceSelection({
      dispatch: store.dispatch,
      device: null,
      platformType: currentMode || 'iOS-Cloud',
      allDevices: devices,
      currentDevice: {}
    })

    return
  }

  const device = entry.device
  const isSameAsSaved =
    preferredDevice && String(getDeviceId(preferredDevice)) === String(getDeviceId(device))

  // 只有恢复同一可选设备时才复用物理设备，切到兜底账号时必须清空。
  bootstrapDeviceSelection({
    dispatch: store.dispatch,
    device,
    allDevices: devices,
    currentDevice: isSameAsSaved ? savedCurrentDevice : {}
  })
}

const AuthProvider = ({ children }) => {
  // ** States
  const [user, setUser] = useState(defaultProvider.user)
  const [loading, setLoading] = useState(defaultProvider.loading)

  const { asPath } = useRouter()

  // ** Hooks
  const router = useRouter()
  useEffect(() => {
    const initAuth = async () => {
      const getParams = url => {
        try {
          url = url.match(/\?([^#]+)/)[1]
          const obj = {}
          const arr = url.split('&')
          for (let i = 0; i < arr.length; i++) {
            let subArr = arr[i].split('=')
            let key = decodeURIComponent(subArr[0])
            let value = decodeURIComponent(subArr[1])
            obj[key] = value
          }

          return obj
        } catch (err) {
          return null
        }
      }

      const urlParams = getParams(location.search)

      let accessToken = window.localStorage.getItem(authConfig.storageTokenKeyName)

      if (urlParams != null) {
        const normalizedInboundToken = urlParams.token || urlParams.accessToken || urlParams.access_token

        // 退出登录
        if (normalizedInboundToken == 'clear') {
          clearReviewFormDraftsForLogout()
          window.localStorage.removeItem('userData')
          window.localStorage.removeItem(authConfig.storageTokenKeyName)
          localStorage.removeItem('Basic')
          localStorage.removeItem('Advanced')
          localStorage.removeItem('DeviceUser')
          localStorage.removeItem('currentDevice', JSON.stringify({}))

          window.location.href = `${clientURL}/login?q=clear`

          return
        }
        if (normalizedInboundToken && normalizedInboundToken != accessToken) {
          localStorage.setItem('Basic', 0)
          localStorage.setItem('Advanced', 0)
          localStorage.setItem('DeviceUser', JSON.stringify({}))
          localStorage.setItem('currentDevice', JSON.stringify({}))
          window.localStorage.setItem(authConfig.storageTokenKeyName, normalizedInboundToken)
        }
      } else if (accessToken == null) {
        if (
          window.location.pathname.substring(1) === 'user-guide-ios' ||
          window.location.pathname.substring(1) === 'user-guide-android' ||
          window.location.pathname.substring(4) === 'user-guide-ios' ||
          window.location.pathname.substring(4) === 'user-guide-android' ||
          window.location.pathname.substring(1) === 'back-up-android' ||
          window.location.pathname.substring(1) === 'back-up-ios' ||
          window.location.pathname.substring(4) === 'back-up-ios' ||
          window.location.pathname.substring(4) === 'back-up-android'
        ) {
          location.href = `${process.env.NEXT_PUBLIC_CLIENT_URL}/login?guide=${location.href}`

          return
        }
        window.location.href = `${process.env.NEXT_PUBLIC_CLIENT_URL}/login`
      }

      let token = window.localStorage.getItem(authConfig.storageTokenKeyName)

      if (token) {
        let user = await getUserInfo()
        localStorage.setItem('userInfo', JSON.stringify(user.data))

        const appBridgeResult = await handleAppDeviceBridge(location.search)

        // App 深链已按 user_device_id 完成设备切换；普通 F5 则在首屏前恢复设备上下文。
        if (!appBridgeResult?.handled && !isBindingFlowPath(window.location.pathname)) {
          try {
            await restoreDeviceSelection()
          } catch (error) {
            // 设备列表暂时获取失败时沿用本地选择，避免阻塞已登录用户。
            console.error('[AuthContext] restore device selection failed', error)
          }
        }

        // let allUserDevice = await getAllUserDevice({})

        // if (allUserDevice.data !== null && allUserDevice.data.length > 0) {
        //   let getDeviceUser = localStorage.getItem('DeviceUser')
        //   if (getDeviceUser == null || getDeviceUser == '{}') {
        //     localStorage.setItem(
        //       'DeviceUser',
        //       JSON.stringify({
        //         username: allUserDevice.data[0].iCloud_username,
        //         password: allUserDevice.data[0].iCloud_password,
        //         temporary: allUserDevice.data[0].temporary,
        //         expire_time: allUserDevice.data[0].expire_time
        //       })
        //     )
        //   } else {
        //     let check = allUserDevice.data.find(row => row.iCloud_username == JSON.parse(getDeviceUser).username)
        //     if (!check) {
        //       localStorage.setItem(
        //         'DeviceUser',
        //         JSON.stringify({
        //           username: allUserDevice.data[0].iCloud_username,
        //           password: allUserDevice.data[0].iCloud_password,
        //           temporary: allUserDevice.data[0].temporary,
        //           expire_time: allUserDevice.data[0].expire_time
        //         })
        //       )
        //     }
        //   }

        //   if (
        //     JSON.parse(localStorage.getItem('DeviceUser')).username == null ||
        //     JSON.parse(localStorage.getItem('DeviceUser')).password == null ||
        //     JSON.parse(localStorage.getItem('DeviceUser')).username == '' ||
        //     JSON.parse(localStorage.getItem('DeviceUser')).password == ''
        //   ) {
        //     localStorage.setItem('Basic', 0)
        //   } else {
        //     localStorage.setItem('Basic', 1)
        //   }

        //   localStorage.setItem('BasicTips', 1)

        //   localStorage.setItem('Advanced', 1)

        //   localStorage.setItem('allDeviceUser', JSON.stringify(allUserDevice.data))
        // } else {
        //   localStorage.setItem('BasicTips', 0)
        //   localStorage.setItem('Basic', 0)
        //   localStorage.setItem('Advanced', 0)
        //   localStorage.setItem('allDeviceUser', JSON.stringify([]))
        // }

        user.role = 'admin'

        setLoading(true)
        setUser(user)
        window.localStorage.setItem('userData', JSON.stringify(user))
        setLoading(false)
      } else {
        setLoading(false)
      }
    }
    initAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogin = (params, errorCallback) => { }

  const handleLogout = () => {
    clearReviewFormDraftsForLogout()
    setUser(null)
    window.localStorage.removeItem('userData')
    window.localStorage.removeItem(authConfig.storageTokenKeyName)
    localStorage.removeItem('Basic')
    localStorage.removeItem('Advanced')
    localStorage.removeItem('DeviceUser')
    localStorage.removeItem('currentDevice', JSON.stringify({}))

    window.location.href = `${clientURL}/login?q=clear`
  }

  const values = {
    user,
    loading,
    setUser,
    setLoading,
    login: handleLogin,
    logout: handleLogout
  }

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
